import { format } from "date-fns";

/**
 * Pure text resolver shared by the live notification path (Notifications.js)
 * and the replay path (notificationsReplay.js), so both resolve the same
 * user-facing text from a notification's technical `type`/`data` instead of
 * ever falling back to displaying the raw `type` string. DAILY_REPORT has no
 * display text of its own (its side effect is triggered by the caller, not
 * here) so it always resolves to null.
 */

/**
 * useLabels() (see src/hooks/useLabels.js) never returns a falsy value: a
 * missing translation resolves to the sentinel `#{locale}_{area}_{label}`
 * (with any params appended), not the bare key. A naive `getLabel(x) || y`
 * therefore never falls through. This treats that sentinel - and any
 * genuinely empty value - as "no label available".
 */
const resolveLabel = (getLabel, key, params) => {
  if (typeof getLabel !== "function" || !key) {
    return null;
  }

  const value = getLabel(key, params);

  if (typeof value !== "string" || !value.trim() || value.startsWith("#")) {
    return null;
  }

  return value;
};

const truncate = (text, maxLength = 140) =>
  text.length > maxLength ? `${text.slice(0, maxLength - 1).trimEnd()}…` : text;

const humanizeNotificationType = (type) => {
  if (typeof type !== "string") {
    return "";
  }

  return type
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

/**
 * Last-resort resolver, used both for the `default` switch branch (a
 * notification type this build doesn't know about yet) and as the safety net
 * inside every known branch when its specific label turns out to be missing.
 * Never returns the raw `notification.type`.
 */
const buildNotificationFallback = (notification, { getLabel } = {}) => {
  const type = notification?.type;

  const existingMessage = notification?.message;
  if (typeof existingMessage === "string" && existingMessage.trim() && existingMessage !== type) {
    return existingMessage.trim();
  }

  const existingDescription = notification?.description;
  if (typeof existingDescription === "string" && existingDescription.trim() && existingDescription !== type) {
    return existingDescription.trim();
  }

  const typeLabel = resolveLabel(getLabel, `notification_${type}`);
  if (typeLabel) {
    return typeLabel;
  }

  const genericLabel = resolveLabel(getLabel, "notificationReceived");
  if (genericLabel) {
    return genericLabel;
  }

  return humanizeNotificationType(type);
};

const APPLY_LABEL_KEYS = {
  APPLY_WORKING_PROTOCOL: "programmingNewWorkingProtocolOp",
  APPLY_WORKING_PROTOCOL_POST: "programmingNewPostWorkingProtocolOp",
  APPLY_FILTERING: "programmingNewFilteringProtocolOp",
  APPLY_STABILIZATION: "programmingNewStabilizationProtocolOp",
  APPLY_SPARKLING: "programmingNewSparklingProtocolOp",
  APPLY_CIP: "programmingNewCIPProtocolOp",
};

export const buildNotificationMessage = (notification, { getLabel, configs, role } = {}) => {
  const type = notification?.type;
  const operationId = Number(notification?.data?.operation_id);
  const operationType = notification?.data?.operation_type;

  switch (type) {
    case "NOTE": {
      const opTypeNewNote = resolveLabel(getLabel, `operationType${operationType}`) || "—";
      const opExtendedIdNewNote = notification?.data?.operation_id ? ' #' + notification?.data?.operation_id : "";
      const headingNewNote = resolveLabel(getLabel, "newNoteFrom");

      if (!headingNewNote) {
        return buildNotificationFallback(notification, { getLabel });
      }

      const heading = `${headingNewNote} ${notification?.from_username} (${opTypeNewNote}${opExtendedIdNewNote})`;
      // The note's own text (validated non-empty server-side, see
      // operations.154 in notificationsHelpers.addNotification) is the most
      // useful detail here - show it instead of just "who/what operation".
      const noteText = typeof notification?.message === "string" ? notification.message.trim() : "";

      return noteText ? `${heading}: "${truncate(noteText)}"` : heading;
    }

    case "APPLY_WORKING_PROTOCOL":
    case "APPLY_WORKING_PROTOCOL_POST":
    case "APPLY_CIP":
    case "APPLY_SPARKLING":
    case "APPLY_FILTERING":
    case "APPLY_STABILIZATION": {
      const date = notification?.data?.start_date ? new Date(notification?.data?.start_date) : null;
      const startDate = date && !isNaN(date) ? format(date, configs?.compactDateFormat) : "-";

      return (
        resolveLabel(getLabel, APPLY_LABEL_KEYS[type], { username: notification?.from_username, start_date: startDate }) ||
        buildNotificationFallback(notification, { getLabel })
      );
    }

    case "NEW_OPERATION":
    case "DELETE_OPERATION":
    case "UPDATE_OPERATION":
    case "START_OPERATION":
    case "COMPLETE_SAMPLING":
    case "COMPLETE_OPERATION":
    case "COMPLETE_OPERATION_BATCH_UPDATE": {
      // COMPLETE_OPERATION_BATCH_UPDATE has the same user-facing meaning as
      // COMPLETE_OPERATION - the only difference is an internal drug-batch
      // stock update the operation also triggered.
      const isComplete = type === "COMPLETE_OPERATION" || type === "COMPLETE_OPERATION_BATCH_UPDATE";
      const opTypeNewOp = resolveLabel(getLabel, `operationType${operationType}`) || "—";
      // custom_name (e.g. an analysis' custom label) is emitted at the
      // notification's top level by addOperation - see note_data in
      // operationsService.js - not nested under `data`; also check the
      // nested spot defensively in case a future producer puts it there.
      const customName = notification?.custom_name || notification?.data?.custom_name;
      const opExtendedIdNewOp = ' #' + (((operationType === 'ANALYSIS') ? (customName || notification?.data?.operation_id) : operationId) || "—");

      const labelKey =
        type === "NEW_OPERATION" ? "programmingNewOp" :
        type === "COMPLETE_SAMPLING" ? "programmingAnalysisSampledOp" :
        isComplete ? (operationType === 'ANALYSIS' ? "programmingAnalysisCompletedOp" : "programmingCompletedOp") :
        type === "DELETE_OPERATION" ? "programmingDeleteOp" :
        type === "START_OPERATION" ? "programmingStartOp" :
        "programmingUpdateOp";

      return (
        resolveLabel(getLabel, labelKey, { operation: `${opTypeNewOp}${opExtendedIdNewOp}`, username: notification?.from_username || '' }) ||
        buildNotificationFallback(notification, { getLabel })
      );
    }

    case "NEW_BATCH":
    case "UPDATE_BATCH": {
      const batchName = notification?.data?.batch_name;
      const isNewBatch = type === "NEW_BATCH";
      const labelKey = batchName
        ? (isNewBatch ? "programmingNewBatchOp" : "programmingUpdateBatchOp")
        : (isNewBatch ? "programmingNewBatchOpGeneric" : "programmingUpdateBatchOpGeneric");

      return (
        resolveLabel(getLabel, labelKey, batchName ? { batch_name: batchName } : undefined) ||
        buildNotificationFallback(notification, { getLabel })
      );
    }

    case "PROTOCOL_APPLICATION_INVALID": {
      return (
        resolveLabel(getLabel, "programmingProtocolInvalidOp", {
          protocol_name: notification?.data?.protocol_name || "—",
          count: notification?.data?.invalid_steps_count ?? "—",
        }) || buildNotificationFallback(notification, { getLabel })
      );
    }

    case "DAILY_REPORT":
      return null;

    default:
      return buildNotificationFallback(notification, { getLabel });
  }
};
